<template>
  <div>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="loading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAllSegments"></b-form-checkbox>
        <span @click="checkedAllSegments = !checkedAllSegments">Strava Segments</span>
        <div style="float: right;">
          <img @click="expanded = !expanded" style="height: 13px; cursor: pointer;"
            :src="expanded ? icons.chevronUp : icons.chevronDown" />
        </div>
      </div>
      <div class="card-body" v-if="expanded">
        <b-form-checkbox class="display-inline marginright5" v-model="active"></b-form-checkbox>
        <span @click="active = !active">Active</span>
        <b-form-checkbox class="display-inline marginright5" v-model="inactive"></b-form-checkbox>
        <span @click="inactive = !inactive">Inactive</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
:global(.strava-segments-pointer-cursor),
:global(.strava-segments-pointer-cursor *) {
  cursor: pointer !important;
}
</style>

<script lang="ts">
import BaseComponent from './Base.vue';
import { EventBus } from '@/ts/EventBus';
import { AlertStatus, DownloadStatus } from '@/ts/types';
import { defineComponent } from "vue";
import L from 'leaflet';
import { DataFilterExtension } from '@deck.gl/extensions';
import { DeckOverlay } from '@deck.gl-community/leaflet';
import { MapLasPathLayer } from '@/ts/MapLasPathLayer';

interface StravaPathBinaryData {
  length: number;
  startIndices: Uint32Array;
  attributes: {
    getPath: { value: Float32Array; size: 2 };
    getFilterValue: { value: Float32Array; size: 1 };
  };
}

interface StravaSegmentsCardState {
  result: any[] | null;
  stravaResults: any[];
  stravaPathLayer: MapLasPathLayer | null;
  stravaOverlay: DeckOverlay | null;
  stravaHoverPolyline: L.Polyline;
  hoveredStravaIndex: number | null;
  mapMouseMoveHandler: ((event: L.LeafletMouseEvent) => void) | null;
  mapMouseOutHandler: (() => void) | null;
  mapClickHandler: ((event: L.LeafletMouseEvent) => void) | null;
}

declare module 'vue' {
  interface ComponentCustomProperties extends StravaSegmentsCardState { }
}

export default defineComponent({
  extends: BaseComponent,
  data() {
    return {
      checkedAllSegments: false,
      loading: false,
      expanded: false,
      active: true,
      inactive: false
    };
  },
  created() {
    this.result = null;
    this.stravaResults = [];
    this.stravaPathLayer = null;
    this.stravaOverlay = null;
    this.stravaHoverPolyline = L.polyline([], {
      color: 'yellow',
      weight: 4,
      opacity: 1,
      interactive: false,
    });
    this.hoveredStravaIndex = null;
    this.mapMouseMoveHandler = null;
    this.mapMouseOutHandler = null;
    this.mapClickHandler = null;
    EventBus.$on('downloadExternalData', () => this.downloadSegmentsStrava());
    EventBus.$on('toggleStravaSegments', () => {
      if (this.result) {
        this.checkedAllSegments = !this.checkedAllSegments;
      }
    });
    EventBus.$on('selectedAreaChanged', (newArea: string, oldArea: string) => this.onSelectedAreaChanged(newArea, oldArea));
  },
  beforeUnmount() {
    if (window.mainmap && this.mapMouseMoveHandler) window.mainmap.off('mousemove', this.mapMouseMoveHandler);
    if (window.mainmap && this.mapMouseOutHandler) window.mainmap.off('mouseout', this.mapMouseOutHandler);
    if (window.mainmap && this.mapClickHandler) window.mainmap.off('click', this.mapClickHandler);
    this.hideStravaHoverPolyline();
    if (window.mainmap && this.stravaOverlay) window.mainmap.removeLayer(this.stravaOverlay);
  },
  methods: {

    isPointInSelectedArea(lat: number | string, lon: number | string) {
      // @ts-ignore
      const areaKey = window.selectedArea;
      if (!areaKey) return true;
      // @ts-ignore
      const area = window.areas[areaKey];
      if (!area || !area[0] || !area[1]) return true;
      const minLat = Math.min(area[0][0], area[1][0]);
      const maxLat = Math.max(area[0][0], area[1][0]);
      const minLon = Math.min(area[0][1], area[1][1]);
      const maxLon = Math.max(area[0][1], area[1][1]);
      const plat = Number(lat);
      const plon = Number(lon);
      if (Number.isNaN(plat) || Number.isNaN(plon)) return false;
      return (plat >= minLat && plat <= maxLat && plon >= minLon && plon <= maxLon);
    },

    isPathInSelectedArea(path: any[]) {
      if (!path || !path.length) return false;
      for (const p of path) {
        if (Array.isArray(p) && p.length >= 2) {
          if (this.isPointInSelectedArea(p[0], p[1])) return true;
        } else if (p && typeof p === 'object') {
          const lat = p.lat ?? p.latitude ?? p[0];
          const lon = p.lon ?? p.lng ?? p.longitude ?? p[1];
          if (lat !== undefined && lon !== undefined && this.isPointInSelectedArea(lat, lon)) return true;
        }
      }
      return false;
    },

    onSelectedAreaChanged(newArea: string, oldArea: string) {
      this.hideStravaHoverPolyline();
      if (this.result) {
        this.processSegmentsStrava();
      }
    },
    downloadSegmentsStrava() {
      this.loading = true;
      const endPoint = window.apiHost + 'api/stringfield/stravasegments/';
      this.downloadUrlWithCache(endPoint, 'stravasegments', 'stravasegments.txt', null, true).then((response) => {
        this.result = response[1].results;
        if (this.checkedAllSegments) this.processSegmentsStrava();
        if (response[0] == DownloadStatus.cache_fresh) {
          this.createAlert(AlertStatus.success, this.$t('toursLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.api_fresh) {
          this.createAlert(AlertStatus.success, this.$t('toursDownloaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.cache_last) {
          this.createAlert(AlertStatus.warning, this.$t('toursLoaded', [response[1].results.length]).toString(), 2000);
        }
      }).catch((error) => {
        this.createAlert(AlertStatus.danger, this.$t('toursError').toString(), 2000);
      }).finally(() => {
        this.loading = false;
      });
    },
    processSegmentsStrava() {
      if (!this.result) return;
      this.hideStravaHoverPolyline();
      const tours: any[] = [];
      for (const responseTour of this.result) {
        // only include segments that have at least one point inside selected area
        if (!this.isPathInSelectedArea(responseTour['points'])) {
          continue;
        }
        const points: Array<[number, number]> = [];
        for (const point of responseTour.points ?? []) {
          const lat = Number(Array.isArray(point) ? point[0] : point?.lat ?? point?.latitude);
          const lon = Number(Array.isArray(point) ? point[1] : point?.lon ?? point?.lng ?? point?.longitude);
          if (Number.isFinite(lat) && Number.isFinite(lon)) points.push([lat, lon]);
        }
        if (points.length >= 2) tours.push({ ...responseTour, normalizedPath: points });
      }
      const positions: number[] = [];
      const startIndices: number[] = [];
      const filterValues: number[] = [];
      for (const segment of tours) {
        startIndices.push(positions.length / 2);
        for (const [lat, lon] of segment.normalizedPath) {
          positions.push(lon, lat);
          filterValues.push(segment.is_active ? 1 : 0);
        }
      }
      startIndices.push(positions.length / 2);
      const data: StravaPathBinaryData = {
        length: tours.length,
        startIndices: Uint32Array.from(startIndices),
        attributes: {
          getPath: { value: Float32Array.from(positions), size: 2 },
          getFilterValue: { value: Float32Array.from(filterValues), size: 1 },
        },
      };
      this.stravaResults = tours;
      this.stravaPathLayer = new MapLasPathLayer({
        id: 'strava-segments',
        data,
        extensions: [new DataFilterExtension({ filterSize: 1 })],
        filterRange: this.getStravaFilterRange(),
        positionFormat: 'XY',
        getColor: [255, 0, 0, 255],
        getWidth: 3,
        widthUnits: 'pixels',
        visible: this.checkedAllSegments,
        pickable: false,
      });
      this.ensureStravaOverlay();
      this.onCheckedAllSegmentsChanged();
    },
    onCheckedAllSegmentsChanged() {
      this.hideStravaHoverPolyline();
      if (!this.stravaPathLayer) {
        if (this.result) this.processSegmentsStrava();
        return;
      }
      this.stravaPathLayer.setVisible(this.checkedAllSegments);
      this.stravaPathLayer.setFilterRange(this.getStravaFilterRange());
      this.updateStravaOverlay();
    },
    getStravaFilterRange(): readonly [number, number] {
      if (this.active && this.inactive) return [0, 1];
      if (this.active) return [1, 1];
      if (this.inactive) return [0, 0];
      return [2, 2];
    },
    ensureStravaOverlay() {
      if (!window.mainmap) return;
      if (!this.stravaOverlay) {
        this.stravaOverlay = new DeckOverlay({ layers: [] });
        window.mainmap.addLayer(this.stravaOverlay);
      }
      if (!this.mapMouseMoveHandler) {
        this.mapMouseMoveHandler = (event: L.LeafletMouseEvent) => this.onStravaMapMouseMove(event);
        window.mainmap.on('mousemove', this.mapMouseMoveHandler);
      }
      if (!this.mapMouseOutHandler) {
        this.mapMouseOutHandler = () => this.hideStravaHoverPolyline();
        window.mainmap.on('mouseout', this.mapMouseOutHandler);
      }
      if (window.isDesktop && !this.mapClickHandler) {
        this.mapClickHandler = (event: L.LeafletMouseEvent) => this.onStravaMapClick(event);
        window.mainmap.on('click', this.mapClickHandler);
      }
    },
    updateStravaOverlay() {
      this.ensureStravaOverlay();
      if (!this.stravaOverlay) return;
      this.stravaOverlay.setProps({ layers: this.stravaPathLayer ? [this.stravaPathLayer.getLayer()] : [] });
    },
    onStravaMapMouseMove(event: L.LeafletMouseEvent) {
      let index = this.checkedAllSegments && this.stravaPathLayer
        ? this.stravaPathLayer.pick(event.latlng.lat, event.latlng.lng)
        : null;
      if (index !== null && !this.isStravaResultVisible(this.stravaResults[index])) index = null;
      if (index === null) {
        this.hideStravaHoverPolyline();
        return;
      }
      window.mainmap.getContainer().classList.add('strava-segments-pointer-cursor');
      if (index === this.hoveredStravaIndex) return;
      const segment = this.stravaResults[index];
      if (!segment) return;
      this.stravaHoverPolyline.setLatLngs(segment.normalizedPath);
      this.hoveredStravaIndex = index;
      if (!window.mainmap.hasLayer(this.stravaHoverPolyline)) this.stravaHoverPolyline.addTo(window.mainmap);
      this.stravaHoverPolyline.bringToFront();
    },
    hideStravaHoverPolyline() {
      if (window.mainmap) {
        window.mainmap.getContainer().classList.remove('strava-segments-pointer-cursor');
        if (window.mainmap.hasLayer(this.stravaHoverPolyline)) this.stravaHoverPolyline.removeFrom(window.mainmap);
      }
      this.hoveredStravaIndex = null;
    },
    onStravaMapClick(event: L.LeafletMouseEvent) {
      if (!this.checkedAllSegments || !this.stravaPathLayer) return;
      const index = this.stravaPathLayer.pick(event.latlng.lat, event.latlng.lng);
      const segment = index === null ? null : this.stravaResults[index];
      if (!segment || !this.isStravaResultVisible(segment)) return;
      window.open(`https://www.strava.com/segments/${segment.id}`, '_blank')?.focus();
    },
    isStravaResultVisible(segment: any): boolean {
      return segment?.is_active ? this.active : this.inactive;
    }
  },
  watch: {
    "checkedAllSegments": [{
      handler: "onCheckedAllSegmentsChanged"
    }],
    "active": [{
      handler: "onCheckedAllSegmentsChanged"
    }],
    "inactive": [{
      handler: "onCheckedAllSegmentsChanged"
    }]
  }
})


</script>
